import {
  BiaCreateItemRq,
  BiaExclusion,
  BiaGroup,
  BiaPayloadItem,
  BiaPlanBody,
  BiaReviewItem,
  BiaTreeNode,
  BiaUploadFileRq,
  CreateBiaSessionForm,
  UpdateBiaImageForm,
  UpdateBiaSessionForm,
} from "../../../interfaces/BulkImageAnalysisV2Object";

/**
 * Request bodies for the Smart photos API, built from UI shapes. One file so a
 * contract change (field names, the CreateProductRq wrapping) is a one-place fix.
 * Mirrors the json-lib `*Rq` classes in com.nine.api.bulkimageanalysis.model.
 */

const trimmedOrUndefined = (value: string | null | undefined): string | undefined => {
  const text = (value ?? "").trim();
  return text === "" ? undefined : text;
};

/** CreateBiaSessionRq. */
export function buildCreateSessionPayload(form: CreateBiaSessionForm): Record<string, unknown> {
  const body: Record<string, unknown> = { businessId: form.businessId };
  if (form.setupId) body.setupId = form.setupId;
  const instructions = trimmedOrUndefined(form.customerInstructions);
  if (instructions) body.customerInstructions = instructions;
  const notes = trimmedOrUndefined(form.curatorNotes);
  if (notes) body.curatorNotes = notes;
  return body;
}

/**
 * UpdateBiaSessionRq. Both fields are sent when given, EMPTY included — clearing
 * a note is a legitimate edit. A field left undefined is not sent at all.
 */
export function buildUpdateSessionPayload(form: UpdateBiaSessionForm): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (form.customerInstructions !== undefined) body.customerInstructions = form.customerInstructions.trim();
  if (form.curatorNotes !== undefined) body.curatorNotes = form.curatorNotes.trim();
  return body;
}

/** BiaUploadUrlsRq. */
export function buildUploadUrlsPayload(files: BiaUploadFileRq[]): Record<string, unknown> {
  return {
    files: files.map((f) => ({
      clientRef: f.clientRef,
      fileName: f.fileName,
      contentType: f.contentType,
      sizeBytes: f.sizeBytes,
    })),
  };
}

/** BiaConfirmUploadsRq. */
export function buildConfirmUploadsPayload(imageIds: string[]): Record<string, unknown> {
  return { imageIds };
}

/** UpdateBiaImageRq. */
export function buildUpdateImagePayload(form: UpdateBiaImageForm): Record<string, unknown> {
  const body: Record<string, unknown> = { excluded: form.excluded };
  if (form.excluded && form.reason) body.reason = form.reason;
  return body;
}

/** ReviseBiaPlanRq. */
export function buildRevisePayload(instruction: string, baseVersion: number): Record<string, unknown> {
  return { instruction: instruction.trim(), baseVersion };
}

/** RestoreBiaPlanRq: `version` is the one to restore, `baseVersion` the current one. */
export function buildRestorePayload(version: number, baseVersion: number): Record<string, unknown> {
  return { version, baseVersion };
}

/** ConfirmBiaPlanRq. */
export function buildConfirmPlanPayload(version: number): Record<string, unknown> {
  return { version };
}

/** UnconfirmBiaPlanRq. */
export function buildUnconfirmPayload(deleteEmptyCreatedSections: boolean): Record<string, unknown> {
  return { deleteEmptyCreatedSections };
}

/** RetryBiaGenerateRq. An empty / absent list means "every failed product". */
export function buildRetryGeneratePayload(productIds?: string[]): Record<string, unknown> {
  return productIds && productIds.length > 0 ? { productIds } : {};
}

// ── Plan body (PUT /plan) ────────────────────────────────────────────────────

function treeNodeToWire(node: BiaTreeNode): Record<string, unknown> {
  return {
    key: node.key,
    name: node.name,
    existingSectionId: node.existingSectionId,
    sectionId: node.sectionId,
    createdByConfirm: node.createdByConfirm,
    productCount: node.productCount,
    children: node.children.map(treeNodeToWire),
  };
}

function groupToWire(group: BiaGroup): Record<string, unknown> {
  return {
    key: group.key,
    imageIds: group.imageIds,
    leafKey: group.leafKey,
    titleHint: group.titleHint,
    variantOfKey: group.variantOfKey,
    confidence: group.confidence,
    reason: group.reason,
  };
}

function exclusionToWire(exclusion: BiaExclusion): Record<string, unknown> {
  return { imageId: exclusion.imageId, reason: exclusion.reason, by: exclusion.by, note: exclusion.note };
}

/** UpdateBiaPlanRq = { baseVersion, plan: BiaPlanBody }. */
export function buildUpdatePlanPayload(baseVersion: number, body: BiaPlanBody): Record<string, unknown> {
  return {
    baseVersion,
    plan: {
      tree: body.tree.map(treeNodeToWire),
      groups: body.groups.map(groupToWire),
      excluded: body.excluded.map(exclusionToWire),
    },
  };
}

// ── Payload / create ─────────────────────────────────────────────────────────

/** A payload item as the review step's item: `{ clientItemId, product }`. Null when generation produced no product. */
export function toReviewItem(item: BiaPayloadItem): BiaReviewItem | null {
  if (!item.product) return null;
  return { clientItemId: item.clientItemId, product: item.product };
}

/**
 * BiaCreateProductsRq = { items: [ { clientItemId, product: CreateProductRq } ] }
 * with CreateProductRq = { product: <Product> }. The review keeps the inner
 * Product (what the existing bulk flow edits), so it is wrapped here, once.
 */
export function buildCreateProductsPayload(items: BiaCreateItemRq[]): Record<string, unknown> {
  return {
    items: items.map((item) => ({
      clientItemId: item.clientItemId,
      product: { product: item.product },
    })),
  };
}

/** The pieces of a review item this feature cares about, read tolerantly from any payload entry. */
export function readReviewItem(entry: unknown): { clientItemId: string | null; product: Record<string, unknown> } {
  if (!entry || typeof entry !== "object") return { clientItemId: null, product: {} };
  const record = entry as Record<string, unknown>;
  const id = typeof record.clientItemId === "string" && record.clientItemId.trim() !== "" ? record.clientItemId.trim() : null;
  const wrapped = "product" in record;
  const source = wrapped ? record.product : record;
  const product = source && typeof source === "object" && !Array.isArray(source) ? (source as Record<string, unknown>) : {};
  return { clientItemId: id, product };
}

/** The same entry without `clientItemId` — what v1 `bulkCreateProduct` should see. */
export function stripClientItemId(entry: unknown): unknown {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
  const { clientItemId: _ignored, ...rest } = entry as Record<string, unknown>;
  void _ignored;
  return rest;
}

export default {
  buildCreateSessionPayload,
  buildUpdateSessionPayload,
  buildUploadUrlsPayload,
  buildConfirmUploadsPayload,
  buildUpdateImagePayload,
  buildRevisePayload,
  buildRestorePayload,
  buildConfirmPlanPayload,
  buildUnconfirmPayload,
  buildRetryGeneratePayload,
  buildUpdatePlanPayload,
  buildCreateProductsPayload,
  toReviewItem,
  readReviewItem,
  stripClientItemId,
};
